import {
  BILLING_CYCLE,
  PRICE_DIMENSION,
  REFUND_ROW_COUNT,
  REFUND_TYPE,
  TENURE_OPERATOR,
  TPriceDimension,
} from "@/features/organization/product/constants/productEnums";
import { dimensionHasDefaultRow, getTypeDimensions, parseUnitPrice } from "@/features/organization/product/utils/productUtils";
import {
  TCreateCombination,
  TCreateProductReqDto,
  TCreateTypePrice,
  TPriceCombinationForm,
  TProductDetail,
  TProductForm,
  TProductWriteBase,
  TRefundCondition,
  TRefundConditionForm,
  TTypePriceForm,
  TUpdateProductReqDto,
} from "@/api/services/main/organization/product/dtos/product.dto";

/** Detail <-> form mapping for the 商品 登録・編集 screen. Numbers are kept as strings in the form. */

let rowSeq = 0;
const rowId = (prefix: string): string => `${prefix}-new-${(rowSeq += 1)}`;
const numStr = (v: number | null | undefined): string | null => (v == null ? null : String(v));
const idStr = (v: number | null | undefined): string | null => (v == null ? null : String(v));

/* ------------------------------------------------------------------ form row factories */

export const emptyTypePriceForm = (dimension: TPriceDimension, isDefault = false): TTypePriceForm => ({
  id: rowId("tp"),
  dimension,
  isDefault,
  nationalityId: null,
  fieldId: null,
  variantValue: null,
  unitPrice: null,
  lowerLimit: null,
  upperLimit: null,
});

/** The pinned 全ての… default row for a dimension (国籍 / 分野 / ティア). その他 has no default row. */
export const defaultTypePriceForm = (dimension: TPriceDimension): TTypePriceForm => emptyTypePriceForm(dimension, true);

/**
 * Guarantee each dimension shows its default row (国籍 / 分野 / ティア) followed by ≥1 free row — so
 * create starts with 2 rows per dimension. その他 has no default row (C3): free rows only. Order:
 * default first.
 */
export const seedTypePrices = (existing: TTypePriceForm[], dimensions: TPriceDimension[]): TTypePriceForm[] => {
  const seeded = dimensions.flatMap((dimension) => {
    const ofDimension = existing.filter((row) => row.dimension === dimension);
    const frees = ofDimension.filter((row) => !row.isDefault);
    if (!dimensionHasDefaultRow(dimension)) {
      // その他 — drop any (defensive) default row and keep only free rows.
      return frees.length ? frees : [emptyTypePriceForm(dimension)];
    }
    const existingDefaults = ofDimension.filter((row) => row.isDefault);
    const defaults = existingDefaults.length ? existingDefaults : [defaultTypePriceForm(dimension)];
    return [...defaults, ...(frees.length ? frees : [emptyTypePriceForm(dimension)])];
  });
  // Keep any rows whose dimension isn't in the active set (defensive; normally none).
  const untouched = existing.filter((row) => !dimensions.includes(row.dimension));
  return [...seeded, ...untouched];
};

export const emptyCombinationForm = (): TPriceCombinationForm => ({
  id: rowId("cb"),
  nationalityId: null,
  fieldId: null,
  variantTypeId: null,
  unitPrice: null,
  discountTokutei2: null,
  memo: null,
});

export const emptyRefundForm = (): TRefundConditionForm => ({
  id: rowId("rf"),
  tenureMonths: null,
  tenureOperator: TENURE_OPERATOR.UNDER,
  refundValue: null,
  refundType: REFUND_TYPE.PERCENT,
});

/** 返金条件 always renders a fixed number of rows — map the defined rows, pad the rest with empty. */
const padRefundForms = (rows: TRefundCondition[]): TRefundConditionForm[] => {
  const mapped: TRefundConditionForm[] = rows.slice(0, REFUND_ROW_COUNT).map((row) => ({
    id: row.id,
    tenureMonths: numStr(row.tenureMonths),
    tenureOperator: row.tenureOperator,
    refundValue: numStr(row.refundValue),
    refundType: row.refundType,
  }));
  while (mapped.length < REFUND_ROW_COUNT) mapped.push(emptyRefundForm());
  return mapped;
};

/** Editable price tables always show ≥1 row — seed one empty row when the list is empty. */
const orOneEmpty = <T>(rows: T[], seed: () => T): T[] => (rows.length ? rows : [seed()]);

/* ------------------------------------------------------------------ detail <-> form */

/** Empty form for 新規登録 (create). */
export const convertProductToFormData = (): TProductForm => ({
  name: "",
  billingCycle: BILLING_CYCLE.SPOT,
  taxRateId: null,
  unit: null,
  unitPrice: null,
  discountTokutei2: null,
  accountCategory: null,
  invoiceDisplayFormat: null,
  isActive: true,
  description: null,
  firstDiscountPeriod: null,
  firstDiscountPrice: null,
  typePrices: seedTypePrices([], getTypeDimensions("", false)),
  nationalityEnabled: true,
  fieldEnabled: false,
  variantEnabled: false,
  combinations: [emptyCombinationForm()],
  refundConditions: padRefundForms([]),
});

/** Map a loaded 商品詳細 into the edit form shape. */
export const convertDetailToFormData = (data: TProductDetail): TProductForm => ({
  name: data.name,
  billingCycle: data.billingCycle,
  taxRateId: idStr(data.taxRateId),
  unit: data.unit,
  unitPrice: numStr(data.unitPrice),
  discountTokutei2: numStr(data.discountTokutei2),
  accountCategory: data.accountCategory,
  invoiceDisplayFormat: data.invoiceDisplayFormat,
  isActive: data.isActive,
  description: data.description ?? null,
  firstDiscountPeriod: numStr(data.firstDiscountPeriod),
  firstDiscountPrice: numStr(data.firstDiscountPrice),
  typePrices: seedTypePrices(
    data.typePrices.map((typePrice) => ({
      // The API returns id as a JSON number; keep it a string so it matches the combination's
      // variantTypeId (string) both as the <Select> option value and in orphan-reset comparisons.
      id: String(typePrice.id),
      dimension: typePrice.dimension,
      isDefault: typePrice.isDefault,
      nationalityId: idStr(typePrice.nationalityId),
      fieldId: idStr(typePrice.fieldId),
      variantValue: typePrice.variantValue,
      unitPrice: numStr(typePrice.unitPrice),
      lowerLimit: numStr(typePrice.lowerLimit),
      upperLimit: numStr(typePrice.upperLimit),
    })),
    getTypeDimensions(data.name, data.isTierDiscountTarget),
  ),
  nationalityEnabled: data.nationalityEnabled,
  fieldEnabled: data.fieldEnabled,
  variantEnabled: data.variantEnabled,
  combinations: orOneEmpty(
    data.combinations.map((item) => ({
      id: String(item.id),
      nationalityId: idStr(item.nationalityId),
      fieldId: idStr(item.fieldId),
      // FK to a tier/other typePrice row; existing rows keep their numeric server id as form-row id.
      variantTypeId: idStr(item.variantTypeId),
      unitPrice: null,
      discountTokutei2: numStr(item.discountTokutei2),
      memo: item.memo,
    })),
    emptyCombinationForm,
  ),
  refundConditions: padRefundForms(data.refundConditions),
});

/* ------------------------------------------------------------------ combination form -> domain */

/**
 * Form combination row -> write-payload row. Omits `id` + `displayOrder`: the server assigns ids and
 * derives 表示順 from the array position. The array order is preserved by the caller's `.map`.
 *
 * The tier/other reference is resolved against `typePrices`: a persisted tier (numeric form-row id)
 * emits `variantTypeId`; a new tier (synthetic `tp-new-N` id) emits `variantTempId` (the correlation
 * key echoed by that tier row's `tempId`) so the server can link both within one request. An
 * unresolved / cleared reference emits both null (= 全て).
 */
export const comboFormToDomain = (
  combinationForm: TPriceCombinationForm,
  typePrices: TTypePriceForm[],
): TCreateCombination => {
  const referenced = combinationForm.variantTypeId
    ? typePrices.find((row) => row.id === combinationForm.variantTypeId)
    : undefined;
  const serverId = referenced ? serverChildId(referenced.id) : undefined;
  return {
    nationalityId: combinationForm.nationalityId ? Number(combinationForm.nationalityId) : null,
    fieldId: combinationForm.fieldId ? Number(combinationForm.fieldId) : null,
    variantTypeId: serverId ?? null,
    variantTempId: referenced && serverId == null ? referenced.id : null,
    discountTokutei2: parseUnitPrice(combinationForm.discountTokutei2),
    memo: combinationForm.memo,
  };
};

/* ------------------------------------------------------------------ form -> create/update payload */

const typePriceFormToDomain = (row: TTypePriceForm): TCreateTypePrice => {
  const base: TCreateTypePrice = {
    dimension: row.dimension,
    isDefault: row.isDefault,
    nationalityId: row.nationalityId ? Number(row.nationalityId) : null,
    fieldId: row.fieldId ? Number(row.fieldId) : null,
    variantValue: row.variantValue,
    unitPrice: parseUnitPrice(row.unitPrice),
    lowerLimit: parseUnitPrice(row.lowerLimit),
    upperLimit: parseUnitPrice(row.upperLimit),
  };
  // A new (unsaved) tier/other row carries a correlation key so a combination can reference it via
  // `variantTempId` before the server has assigned a real id.
  const isTierOrOther = row.dimension === PRICE_DIMENSION.TIER || row.dimension === PRICE_DIMENSION.OTHER;
  if (isTierOrOther && serverChildId(row.id) == null) base.tempId = row.id;
  return base;
};

/**
 * Keep any row carrying a value or a price; drop fully-empty rows — including a default row with no
 * unitPrice. An empty default carries nothing the server needs, so there's no reason to submit it.
 * All dimensions are submitted regardless of the enabled toggles — the flags gate display/usage,
 * not persistence.
 */
const isMeaningfulTypePrice = (row: TTypePriceForm): boolean =>
  !!row.nationalityId ||
  !!row.fieldId ||
  !!row.variantValue?.trim() ||
  parseUnitPrice(row.unitPrice) != null;

const refundFormToDomain = (row: TRefundConditionForm): Omit<TRefundCondition, "id" | "order"> => ({
  tenureMonths: parseUnitPrice(row.tenureMonths),
  tenureOperator: row.tenureOperator,
  refundValue: parseUnitPrice(row.refundValue),
  refundType: row.refundType,
});

/** 返金条件 renders a fixed set of rows; only rows with a 在職期間 or 返金額 are submitted. */
const isMeaningfulRefund = (row: TRefundConditionForm): boolean =>
  parseUnitPrice(row.tenureMonths) != null || parseUnitPrice(row.refundValue) != null;

/** A 組み合わせ row is worth submitting once it carries any condition, discount, or memo. */
const isMeaningfulCombination = (row: TPriceCombinationForm): boolean =>
  !!row.nationalityId ||
  !!row.fieldId ||
  !!row.variantTypeId ||
  parseUnitPrice(row.discountTokutei2) != null ||
  !!row.memo?.trim();

export type TConvertPayloadOptions = {
  /** ティア対象 (from master / detail) — persisted so the ティア segment shows on reload. */
  isTierDiscountTarget: boolean;
};

/** Product-level (non-child) fields shared by the create + update payloads. Numbers parsed from strings. */
const buildWriteBase = (form: TProductForm, { isTierDiscountTarget }: TConvertPayloadOptions): TProductWriteBase => ({
  name: form.name.trim(),
  billingCycle: form.billingCycle,
  taxRateId: form.taxRateId ? Number(form.taxRateId) : null,
  unit: form.unit,
  unitPrice: parseUnitPrice(form.unitPrice),
  discountTokutei2: parseUnitPrice(form.discountTokutei2),
  accountCategory: form.accountCategory,
  isTierDiscountTarget,
  invoiceDisplayFormat: form.invoiceDisplayFormat,
  isActive: form.isActive,
  description: form.description,
  firstDiscountPeriod: parseUnitPrice(form.firstDiscountPeriod),
  firstDiscountPrice: parseUnitPrice(form.firstDiscountPrice),
  nationalityEnabled: form.nationalityEnabled,
  fieldEnabled: form.fieldEnabled,
  variantEnabled: form.variantEnabled,
});

/** Numeric server id of a form row, or undefined for a row added in the form (synthetic id). */
const serverChildId = (formId: string): number | undefined => (/^\d+$/.test(formId) ? Number(formId) : undefined);

/** POST payload — every child row is new (no id); server derives 表示順 from array position. */
export const convertFormToCreatePayload = (form: TProductForm, opts: TConvertPayloadOptions): TCreateProductReqDto => ({
  ...buildWriteBase(form, opts),
  typePrices: form.typePrices.filter(isMeaningfulTypePrice).map(typePriceFormToDomain),
  combinations: form.combinations
    .filter(isMeaningfulCombination)
    .map((row) => comboFormToDomain(row, form.typePrices)),
  refundConditions: form.refundConditions.filter(isMeaningfulRefund).map(refundFormToDomain),
});

/** PUT payload — existing rows keep their server id (update in place); new rows omit it (delete-missing on server). */
export const convertFormToUpdatePayload = (form: TProductForm, opts: TConvertPayloadOptions): TUpdateProductReqDto => ({
  ...buildWriteBase(form, opts),
  typePrices: form.typePrices.filter(isMeaningfulTypePrice).map((row) => {
    const base = typePriceFormToDomain(row);
    const id = serverChildId(row.id);
    return id == null ? base : { ...base, id };
  }),
  combinations: form.combinations.filter(isMeaningfulCombination).map((row) => {
    const base = comboFormToDomain(row, form.typePrices);
    const id = serverChildId(row.id);
    return id == null ? base : { ...base, id };
  }),
  refundConditions: form.refundConditions.filter(isMeaningfulRefund).map((row) => {
    const base = refundFormToDomain(row);
    const id = serverChildId(row.id);
    return id == null ? base : { ...base, id };
  }),
});
